import {
  AUDIO_GAIN_MAX,
  AUDIO_GAIN_MIN,
  AUDIO_HIT_URLS,
  AUDIO_SPEED_NORM_MPS,
  EDGE_QUALITY,
  type EdgeQuality,
} from '../config/constants';
import { getSfxDestination } from './audioManager';

/**
 * Per-quality hit feedback: one voice per {@link EdgeQuality} — the three cut colours
 * (good/ok/flat = verde/giallo/rosso) plus the colpo di punta (thrust). Per-shot volume
 * scales with tip speed; the master SFX volume is applied by the shared bus.
 *
 * Assets expected at the {@link AUDIO_HIT_URLS} paths. Missing files degrade silently.
 * Plays through the app-level {@link getSfxDestination} bus, not its own context.
 */
export class HitAudio {
  private ctx: AudioContext | null = null;
  private bus: GainNode | null = null;
  private readonly buffers: Partial<Record<EdgeQuality, AudioBuffer>> = {};

  async init(): Promise<void> {
    const dest = getSfxDestination();
    if (!dest) return;
    this.ctx = dest.ctx;
    this.bus = dest.bus;
    await Promise.all(
      (Object.keys(AUDIO_HIT_URLS) as EdgeQuality[]).map(async (quality) => {
        const buf = await this.load(AUDIO_HIT_URLS[quality]).catch(() => null);
        if (buf) this.buffers[quality] = buf;
      }),
    );
  }

  play(quality: EdgeQuality, tipSpeedMPerSec: number): void {
    if (!this.ctx || !this.bus) return;
    const buf = this.buffers[quality] ?? this.buffers[EDGE_QUALITY.good];
    if (!buf) return;

    const src = this.ctx.createBufferSource();
    src.buffer = buf;
    const gain = this.ctx.createGain();
    gain.gain.value = clamp(tipSpeedMPerSec / AUDIO_SPEED_NORM_MPS, AUDIO_GAIN_MIN, AUDIO_GAIN_MAX);
    src.connect(gain).connect(this.bus);
    src.start();
  }

  private async load(url: string): Promise<AudioBuffer> {
    if (!this.ctx) throw new Error('audio context not ready');
    const res = await fetch(url);
    const arr = await res.arrayBuffer();
    return this.ctx.decodeAudioData(arr);
  }
}

function clamp(v: number, lo: number, hi: number): number {
  return Math.min(hi, Math.max(lo, v));
}
